import './customerPortal.css';

import { ApolloClient, useApolloClient } from '@apollo/client';
import React, { useState } from 'react';
import { Redirect } from 'react-router-dom';

import { DocumentDownloadLink } from '../../generated/graphql';
import {
  OPEN_CUSTOMER_DOCUMENT,
  OpenCustomerDocumentQuery,
  OpenCustomerDocumentQueryVariables
} from '../../graphql/openCustomerDocument';
import { safeAssignLocation, safeOpenUrl } from '../../utils/safeRedirect';

interface DownloadPortalParams {
  policyId: string;
  documentDownloadLink: DocumentDownloadLink;
}

/**
 * NCP-4701: route a document download through the audited backend query
 * (`openCustomerDocument`) so the access is recorded, then open the
 * server-returned link in a new tab.
 *
 * The tab is opened synchronously inside the click gesture (before the async
 * query) so browsers do not treat the post-await navigation as a blocked
 * popup. If the audited query is unavailable (e.g. the NCP-4701 backend PR is
 * not yet deployed) or returns nothing, we fall back to the presigned link
 * from `getDocumentInformation` so the download flow never breaks.
 */
const auditAndOpenDocument = (
  client: ApolloClient<unknown>,
  policyId: string,
  documentType: string,
  fallbackLink: string
): void => {
  const newTab = window.open('', '_blank');

  const openTarget = (target: string) => {
    // NCP-4847: validate the backend-supplied URL (scheme allowlist) before
    // assigning it to a location / opening it, to prevent XSS / open-redirect
    // (CWE-601) via javascript:/data: payloads.
    if (newTab) {
      safeAssignLocation(newTab, target);
    } else {
      safeOpenUrl(target);
    }
  };

  if (!policyId || !documentType) {
    openTarget(fallbackLink);
    return;
  }

  client
    .query<OpenCustomerDocumentQuery, OpenCustomerDocumentQueryVariables>({
      query: OPEN_CUSTOMER_DOCUMENT,
      variables: { policyId, documentType },
      fetchPolicy: 'network-only'
    })
    .then(({ data }) => {
      openTarget(data?.openCustomerDocument || fallbackLink);
    })
    .catch(() => {
      // Backend query unavailable (e.g. NCP-4701 backend not yet deployed) —
      // degrade gracefully to the original presigned link.
      openTarget(fallbackLink);
    });
};

const DownloadPortal: React.FC<DownloadPortalParams> = ({
  policyId,
  documentDownloadLink
}) => {
  const client = useApolloClient();
  const [goBack, setGoBack] = useState<boolean>(false);
  if (goBack) {
    return <Redirect push to="/customer-portal" />;
  }
  return (
    <div className="grid grid-cols-2 pl-10 place-items-start gap-4 pt-10 ">
      <div className="row-start-1 text-xl uppercase font-bold">documents</div>
      <div className="row-start-2 font-bold">Policy Document</div>
      <div className="row-start-2">
        <button
          className="download-button"
          onClick={() => {
            auditAndOpenDocument(
              client,
              policyId,
              'policyDocument',
              documentDownloadLink.policyDocument
            );
          }}
        >
          Download
        </button>
      </div>

      <div className="row-start-3 font-bold">Payment Receipt</div>
      <div className="row-start-3">
        <button
          className="download-button"
          onClick={() => {
            auditAndOpenDocument(
              client,
              policyId,
              'PaymentReceipt',
              documentDownloadLink.PaymentReceipt
            );
          }}
        >
          Download
        </button>
      </div>

      <div className="row-start-4 font-bold">T&C Document</div>
      <div className="row-start-4">
        <button
          className="download-button"
          onClick={() => {
            auditAndOpenDocument(
              client,
              policyId,
              'T_CDocument',
              documentDownloadLink.T_CDocument
            );
          }}
        >
          Download
        </button>
      </div>

      <div className="row-start-5 col-span-2 pt-5">
        <button
          onClick={() => {
            setGoBack(true);
          }}
          className="bg-orange py-2 px-6 text-white font-bold rounded-md uppercase"
        >
          Back
        </button>
      </div>
    </div>
  );
};
export default DownloadPortal;
